import { Button, Input, Select, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { SlugField } from '@/components/form/SlugField';
import { SubmitButton } from '@/components/SubmitButton';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import type { DepartmentInput, DepartmentRow, FacultyRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/departments`, gated on `faculties.update`.
 *
 * Scoped, unlike the faculty list itself: a department is part of a faculty
 * rather than a record in its own right, so a Faculty Admin sees theirs and
 * nobody else's. The faculty selector is locked to theirs for the same reason —
 * §3's "the faculty selector is locked and pre-filled", applied here because
 * the server refuses anything else.
 */
export function DepartmentsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const { hasRole } = useAuth();
  const list = useListParams('sort_order');

  const faculties = useResourceList<FacultyRow>('faculties', { per_page: 100, sort: 'sort_order' });

  const columns: Column<DepartmentRow>[] = [
    {
      key: 'name',
      label: t('academic.departments.columns.name'),
      render: (row) => (
        <span className="font-semibold text-primary">{valueFor(row.name, locale)}</span>
      ),
    },
    {
      key: 'faculty',
      label: t('academic.departments.columns.faculty'),
      render: (row) => (
        <span className="text-secondary">
          {row.faculty ? valueFor(row.faculty.name, locale) : '—'}
        </span>
      ),
    },
    {
      key: 'head',
      label: t('academic.departments.columns.head'),
      render: (row) => <span className="text-caption text-secondary">{row.head?.name ?? '—'}</span>,
    },
    {
      key: 'programs_count',
      label: t('academic.departments.columns.programs'),
      align: 'end',
      // `programs.department_id` is `nullOnDelete()`, so removing a department
      // detaches its programmes silently — the count is the warning.
      render: (row) => (row.programs_count === null ? '—' : row.programs_count),
    },
    {
      key: 'sort_order',
      label: t('content.fields.order'),
      sortable: true,
      align: 'end',
      render: (row) => row.sort_order,
    },
  ];

  return (
    <ResourceListScreen
      resource="departments"
      // §2 gates this screen on `faculties.update`, not on a permission of its
      // own — so the create and delete buttons ask that too.
      permission="faculties"
      title={t('nav.items.departments')}
      intro={t('academic.departments.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.name, locale)}
      defaultSort="sort_order"
      emptyTitle={t('academic.departments.empty.title')}
      emptyBody={t('academic.departments.empty.body')}
      newLabel={t('academic.departments.new')}
      deleteTitle={t('academic.departments.delete.title')}
      deleteBody={(label) => t('academic.departments.delete.body', { label })}
      filters={
        // A Faculty Admin sees one faculty's departments and nothing else, so
        // a filter over a list of one is noise.
        hasRole('faculty-admin') ? undefined : (
          <Select
            label={t('academic.departments.columns.faculty')}
            hideLabel
            value={String(list.params.faculty_id ?? '')}
            onChange={(event) => list.setFilter('faculty_id', event.target.value || null)}
            options={[
              { value: '', label: t('academic.anyFaculty') },
              ...(faculties.data?.data ?? []).map((faculty) => ({
                value: String(faculty.id),
                label: valueFor(faculty.name, locale),
              })),
            ]}
          />
        )
      }
    />
  );
}

interface Fields {
  faculty_id: number | '';
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  head_staff_id: string;
  sort_order: number;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { hasRole, user } = useAuth();

  const scoped = hasRole('faculty-admin');

  const faculties = useResourceList<FacultyRow>('faculties', { per_page: 100, sort: 'sort_order' });

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<DepartmentRow, Fields, DepartmentInput>({
      resource: 'departments',
      recordId,
      empty: () => empty(scoped ? (user?.faculty?.id ?? '') : ''),
      toFields,
      toInput,
      errorFields: ['faculty_id', 'name', 'slug', 'description', 'head_staff_id', 'sort_order'],
    });

  const { register, setValue, formState } = form;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-3xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('academic.departments.new') : t('academic.departments.edit')}
        </h1>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/departments')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="space-y-5">
        <Select
          label={t('academic.departments.columns.faculty')}
          hint={scoped ? t('academic.lockedToYourFaculty') : undefined}
          {...register('faculty_id', { valueAsNumber: true })}
          // §3 — a Faculty Admin has one legal answer, and the server refuses
          // the others. Offering them would be offering a 422.
          disabled={scoped}
          error={formState.errors.faculty_id?.message}
          options={[
            { value: '', label: t('academic.chooseFaculty') },
            ...(faculties.data?.data ?? []).map((faculty) => ({
              value: String(faculty.id),
              label: valueFor(faculty.name, locale),
            })),
          ]}
        />

        <TranslatableField
          label={t('academic.departments.fields.name')}
          name="name"
          value={values.name}
          onChange={(next) => setValue('name', next, { shouldDirty: true })}
          error={formState.errors.name?.message}
        />

        <SlugField
          value={values.slug}
          onChange={(next) => setValue('slug', next, { shouldDirty: true })}
          original={record?.slug}
          // A department has no page of its own, so a slug change breaks no
          // inbound link — the warning would be a false alarm.
          published={false}
          error={formState.errors.slug?.message}
        />

        <TranslatableField
          label={t('content.fields.description')}
          name="description"
          multiline
          rows={3}
          value={values.description}
          onChange={(next) => setValue('description', next, { shouldDirty: true })}
          required={false}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            {...register('head_staff_id')}
            type="number"
            min={1}
            label={t('academic.departments.fields.head')}
            hint={t('academic.departments.fields.headHint')}
            error={formState.errors.head_staff_id?.message}
          />

          <Input
            {...register('sort_order', { valueAsNumber: true })}
            type="number"
            min={0}
            label={t('content.fields.order')}
            hint={t('content.fields.orderHint')}
          />
        </div>

        {record?.head ? (
          <p className="text-caption text-secondary">
            {t('academic.departments.currentHead', { name: record.head.name })}
          </p>
        ) : null}

        <TranslationProgress
          fields={{
            name: { label: t('academic.departments.fields.name'), value: values.name },
            description: { label: t('content.fields.description'), value: values.description },
          }}
        />

        <div className="flex justify-end">
          <SubmitButton loading={isSubmitting}>{t('editor.publishing.save')}</SubmitButton>
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(facultyId: number | ''): Fields {
  return {
    // Pre-filled for a scoped editor, so they see the faculty their department
    // will belong to rather than discovering it from a 422.
    faculty_id: facultyId,
    name: {},
    slug: {},
    description: {},
    head_staff_id: '',
    sort_order: 0,
  };
}

function toFields(row: DepartmentRow): Fields {
  return {
    faculty_id: row.faculty_id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    head_staff_id: row.head_staff_id === null ? '' : String(row.head_staff_id),
    sort_order: row.sort_order,
  };
}

function toInput(form: Fields): DepartmentInput {
  return {
    faculty_id: Number(form.faculty_id),
    name: form.name,
    slug: form.slug,
    description: form.description,
    head_staff_id: form.head_staff_id === '' ? null : Number(form.head_staff_id),
    sort_order: Number(form.sort_order),
  };
}
